import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CoreService } from 'src/app/core/helpers/core.service';

interface RewardItem {
  title: string;
  imageUrl: string;
}

@Component({
    selector: 'loyaltic-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    standalone: false
})
export class CarouselComponent implements OnInit, OnDestroy {
  private readonly assetBaseUrl = 'https://assets.qnordicsgames.com/image/upload/v1/FINKFCCHRISTMAS26/rewards';

  rewards: any[] = [
  ];

  itemsPerViewport = {
    desktop: 6,
    tablet: 3,
    mobile: 1
  };

  breakpoints = {
    desktop: 1280,
    tablet: 768
  };

  visibleCount = this.itemsPerViewport.desktop;
  startIndex = 0;
  slideDirection: 'next' | 'prev' | null = null;
  private slideTimer?: ReturnType<typeof setTimeout>;

  constructor(private coreService: CoreService) {
    this.getRewards();
  }

  ngOnInit(): void {
    
  }

  ngOnDestroy(): void {
    this.clearSlideTimer();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateVisibleCount();
  }

  next(): void {
    if (!this.canScrollNext) {
      return;
    }

    this.startIndex = this.getCircularIndex(this.startIndex + this.visibleCount);
    this.triggerSlide('next');
  }

  prev(): void {
    if (!this.canScrollPrev) {
      return;
    }

    this.startIndex = this.getCircularIndex(this.startIndex - this.visibleCount);
    this.triggerSlide('prev');
  }

  getRewards() {
    this.coreService.getRewardList().subscribe({
      next: (response:any) => {
        console.log("=response", response)
        this.rewards = response.result.rewards.filter((reward:any) => reward.order != 0);
        this.updateVisibleCount();
      },
      error: (error) => {
        // Geo-restriction errors will be handled by the interceptor and app.component
        // Just log other errors
        if (error.status !== 403 || error.error?.message !== 'This service is only available from Finland.') {
          console.error('Error fetching rewards:', error);
        }
      }
    })
  }

  get visibleRewards(): RewardItem[] {
    if (!this.rewards.length) {
      return [];
    }

    const count = Math.min(this.visibleCount, this.rewards.length);

    return Array.from({ length: count }, (_, idx) => {
      const rewardIndex = this.getCircularIndex(this.startIndex + idx);
      return this.rewards[rewardIndex];
    });
  }

  get hasControls(): boolean {
    return this.rewards.length > this.visibleCount;
  }

  get canScrollPrev(): boolean {
    return this.hasControls;
  }

  get canScrollNext(): boolean {
    return this.hasControls;
  }

  get previousReward(): RewardItem | null {
    if (!this.rewards.length) {
      return null;
    }

    if (!this.hasControls) {
      return null;
    }

    const index = this.getCircularIndex(this.startIndex - 1);
    return this.rewards[index];
  }

  get nextReward(): RewardItem | null {
    if (!this.rewards.length) {
      return null;
    }

    if (!this.hasControls) {
      return null;
    }

    const index = this.getCircularIndex(this.startIndex + this.visibleCount);
    return this.rewards[index];
  }

  get maxStartIndex(): number {
    return Math.max(0, this.rewards.length - this.visibleCount);
  }

  get visibleRangeEnd(): number {
    return Math.min(this.startIndex + this.visibleCount, this.rewards.length);
  }

  get activeVisibleIndex(): number {
    if (!this.rewards.length) {
      return 0;
    }

    const count = Math.min(this.visibleCount, this.rewards.length);
    return Math.floor((count - 1) / 2);
  }

  private updateVisibleCount(): void {
    const width = window.innerWidth;
    let count = this.itemsPerViewport.mobile;

    if (width >= this.breakpoints.desktop) {
      count = this.itemsPerViewport.desktop;
    } else if (width >= this.breakpoints.tablet) {
      count = this.itemsPerViewport.tablet;
    }

    this.visibleCount = Math.min(count, this.rewards.length || count);
    this.startIndex = this.rewards.length ? this.getCircularIndex(this.startIndex) : 0;
  }

  private triggerSlide(direction: 'next' | 'prev'): void {
    this.slideDirection = direction;
    this.clearSlideTimer();

    this.slideTimer = window.setTimeout(() => {
      this.slideDirection = null;
    }, 520);
  }

  private clearSlideTimer(): void {
    if (this.slideTimer) {
      clearTimeout(this.slideTimer);
      this.slideTimer = undefined;
    }
  }

  private getCircularIndex(index: number): number {
    if (!this.rewards.length) {
      return 0;
    }

    const mod = index % this.rewards.length;
    return mod >= 0 ? mod : mod + this.rewards.length;
  }
}
